import {
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Box
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { format, parseISO, subDays, subMonths } from "date-fns";
import { useState, useEffect, useMemo } from "react";
import Drawer from "shared/Drawer";
import KeyboardDatePicker from "shared/KeyboardDatePicker";

import { useStyles } from "./styles";

const formatDate = date => format(date, "yyyy-MM-dd");

// Função para obter as datas dinâmicamente
const getInitialEntries = () => {
  const today = new Date();
  const yesterday = subDays(today, 1);

  return [
    {
      id: 0,
      name: "Todos os períodos",
      startDate: null,
      endDate: null
    },
    {
      id: 1,
      name: "Hoje",
      startDate: formatDate(today),
      endDate: formatDate(today)
    },
    {
      id: 2,
      name: "Ontem",
      startDate: formatDate(yesterday),
      endDate: formatDate(yesterday)
    },
    {
      id: 3,
      name: "Último mês",
      startDate: formatDate(subMonths(today, 1)),
      endDate: formatDate(today)
    },
    {
      id: 4,
      name: "Últimos 15 dias",
      startDate: formatDate(subDays(today, 15)),
      endDate: formatDate(today)
    },
    {
      id: 5,
      name: "Últimos 7 dias",
      startDate: formatDate(subDays(today, 7)),
      endDate: formatDate(today)
    },
    {
      id: 6,
      name: "Período customizado",
      startDate: "custom",
      endDate: "custom"
    }
  ];
};

const FilterDrawer = ({
  open,
  onClose,
  onApply,
  onClear,
  initialParams,
  reasons,
  categories,
  subcategories
}) => {
  const classes = useStyles();

  // Recalcula as datas sempre que o drawer for aberto
  const initialEntries = useMemo(() => getInitialEntries(), [open]);

  // Determina qual período predefinido está sendo usado
  const getInitialSelectedEntry = () => {
    // Se não há período definido, retorna "Todos os períodos"
    if (!initialParams?.startDate || !initialParams?.endDate) {
      return initialEntries[0]; // Todos os períodos
    }

    const startDate = initialParams.startDate;
    const endDate = initialParams.endDate;

    // Verifica se corresponde a algum período predefinido
    const matchingEntry = initialEntries.find(
      entry =>
        entry.startDate === startDate &&
        entry.endDate === endDate &&
        entry.startDate !== "custom" &&
        entry.startDate !== null
    );
    return matchingEntry || initialEntries[6]; // custom
  };

  const initialSelectedEntry = getInitialSelectedEntry();

  const [selectedEntry, setSelectedEntry] = useState(initialSelectedEntry);
  const [customStartDate, setCustomStartDate] = useState(
    initialParams?.startDate ? parseISO(initialParams.startDate) : new Date()
  );
  const [customEndDate, setCustomEndDate] = useState(
    initialParams?.endDate ? parseISO(initialParams.endDate) : new Date()
  );
  const [selectedReasons, setSelectedReasons] = useState(
    initialParams?.reasonId || []
  );
  const [selectedCategories, setSelectedCategories] = useState(
    initialParams?.categoryId || []
  );
  const [selectedSubcategories, setSelectedSubcategories] = useState(
    initialParams?.subcategoryId || []
  );
  const [groupBy, setGroupBy] = useState(initialParams?.groupBy || []);

  // Sincroniza estados quando initialParams mudam
  useEffect(() => {
    if (!initialParams) return;

    setSelectedEntry(getInitialSelectedEntry());
    setCustomStartDate(
      initialParams.startDate ? parseISO(initialParams.startDate) : new Date()
    );
    setCustomEndDate(
      initialParams.endDate ? parseISO(initialParams.endDate) : new Date()
    );
    setSelectedReasons(initialParams.reasonId || []);
    setSelectedCategories(initialParams.categoryId || []);
    setSelectedSubcategories(initialParams.subcategoryId || []);
    setGroupBy(initialParams.groupBy || []);
  }, [initialParams]);

  const handleRadioChange = event => {
    const entryId = Number(event.target.value);
    const entry = initialEntries.find(e => e.id === entryId);
    setSelectedEntry(entry);

    if (entry.startDate === null) {
      setGroupBy([]);
    }

    if (entry.startDate && entry.startDate !== "custom") {
      setCustomStartDate(parseISO(entry.startDate));
      setCustomEndDate(parseISO(entry.endDate));
    }
  };

  const handleApply = () => {
    const isCustom = selectedEntry.startDate === "custom";
    const isAllPeriods = selectedEntry.startDate === null;

    let startDate;
    let endDate;

    if (isAllPeriods) {
      startDate = undefined;
      endDate = undefined;
    } else if (isCustom) {
      startDate = formatDate(customStartDate);
      endDate = formatDate(customEndDate);
    } else {
      startDate = selectedEntry.startDate;
      endDate = selectedEntry.endDate;
    }

    const params = {
      startDate,
      endDate,
      reasonId: selectedReasons.length > 0 ? selectedReasons : undefined,
      categoryId:
        selectedCategories.length > 0 ? selectedCategories : undefined,
      subcategoryId:
        selectedSubcategories.length > 0 ? selectedSubcategories : undefined,
      groupBy: groupBy.length > 0 ? groupBy : undefined,
      page: 1
    };

    onApply(params);
    onClose();
  };

  const handleClear = () => {
    setSelectedEntry(initialEntries[0]); // Todos os períodos
    setCustomStartDate(new Date());
    setCustomEndDate(new Date());
    setSelectedReasons([]);
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setGroupBy([]);

    onClear();
    onClose();
  };

  return (
    <Drawer open={open} onClose={onClose} title="Filtros" anchor="right">
      <Box className={classes.drawerContent}>
        <Box className={classes.scrollableContent}>
          {/* Filtro de Período */}
          <Box className={classes.filterSection}>
            <Typography className={classes.sectionTitle}>Período</Typography>
            <Box className={classes.radioSection}>
              <RadioGroup value={selectedEntry.id} onChange={handleRadioChange}>
                {initialEntries.map(item => (
                  <FormControlLabel
                    key={item.id}
                    value={item.id}
                    control={<Radio size="small" color="primary" />}
                    label={item.name}
                  />
                ))}
              </RadioGroup>
            </Box>

            {selectedEntry.startDate === "custom" && (
              <Box className={classes.customDateSection}>
                <KeyboardDatePicker
                  label="Data inicial"
                  fullWidth
                  disableToolbar
                  variant="inline"
                  format="dd/MM/yyyy"
                  inputVariant="outlined"
                  views={["date"]}
                  value={customStartDate}
                  onChange={date => setCustomStartDate(date)}
                  maxDate={customEndDate}
                  invalidDateMessage="Data inválida"
                  maxDateMessage="Data inicial não pode ser posterior à data final"
                  KeyboardButtonProps={{
                    "aria-label": "mudar data inicial"
                  }}
                  className={classes.dateInput}
                />
                <KeyboardDatePicker
                  label="Data final"
                  fullWidth
                  disableToolbar
                  variant="inline"
                  format="dd/MM/yyyy"
                  inputVariant="outlined"
                  views={["date"]}
                  value={customEndDate}
                  onChange={date => setCustomEndDate(date)}
                  minDate={customStartDate}
                  invalidDateMessage="Data inválida"
                  minDateMessage="Data final não pode ser anterior à data inicial"
                  KeyboardButtonProps={{
                    "aria-label": "mudar data final"
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Filtro de Motivos */}
          <Box className={`${classes.filterSection} ${classes.inputSection}`}>
            <Typography className={classes.sectionTitle}>Motivo</Typography>
            <Autocomplete
              multiple
              options={reasons}
              getOptionLabel={option => option.label}
              value={reasons.filter(r => selectedReasons.includes(r.id))}
              onChange={(_, newValue) => {
                setSelectedReasons(newValue.map(v => v.id));
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Selecione os motivos"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    label={option.label}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </Box>

          {/* Filtro de Categorias */}
          <Box className={`${classes.filterSection} ${classes.inputSection}`}>
            <Typography className={classes.sectionTitle}>Categoria</Typography>
            <Autocomplete
              multiple
              options={categories}
              getOptionLabel={option => option.label}
              value={categories.filter(c => selectedCategories.includes(c.id))}
              onChange={(_, newValue) => {
                setSelectedCategories(newValue.map(v => v.id));
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Selecione as categorias"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    label={option.label}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </Box>

          {/* Filtro de Subcategorias */}
          <Box className={`${classes.filterSection} ${classes.inputSection}`}>
            <Typography className={classes.sectionTitle}>
              Subcategoria
            </Typography>
            <Autocomplete
              multiple
              options={subcategories}
              getOptionLabel={option => option.label}
              value={subcategories.filter(s =>
                selectedSubcategories.includes(s.id)
              )}
              onChange={(_, newValue) => {
                setSelectedSubcategories(newValue.map(v => v.id));
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Selecione as subcategorias"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    label={option.label}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </Box>

          {/* Filtro de Agrupamento */}
          <Box className={classes.filterSection}>
            <Typography className={classes.sectionTitle}>
              Opções de visualização
            </Typography>
            {selectedEntry.startDate === null && (
              <Typography
                variant="caption"
                style={{ color: "#f44336", marginBottom: "0.5rem" }}
              >
                Selecione um período para habilitar o agrupamento
              </Typography>
            )}
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupBy.includes("product")}
                    disabled={selectedEntry.startDate === null}
                    onChange={e => {
                      if (e.target.checked) {
                        setGroupBy([...groupBy, "product"]);
                      } else {
                        setGroupBy(groupBy.filter(item => item !== "product"));
                      }
                    }}
                    color="primary"
                  />
                }
                label="Agrupar por produto"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupBy.includes("reason")}
                    disabled={selectedEntry.startDate === null}
                    onChange={e => {
                      if (e.target.checked) {
                        setGroupBy([...groupBy, "reason"]);
                      } else {
                        setGroupBy(groupBy.filter(item => item !== "reason"));
                      }
                    }}
                    color="primary"
                  />
                }
                label="Agrupar por motivo"
              />
            </FormGroup>
          </Box>
        </Box>

        {/* Botões de ação */}
        <Box className={classes.buttonsContainer}>
          <Button
            variant="outlined"
            color="default"
            onClick={handleClear}
            fullWidth
            className={classes.clearButton}
          >
            Limpar filtros
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApply}
            fullWidth
          >
            Aplicar filtros
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;
